import type { MediaRef } from '@ezishizi/content-schema';
import { log } from './diag.ts';
import { sfx } from './sfx.ts';

/**
 * One audio channel for the whole app. Three browser facts shape all of it.
 *
 * 1. Playback has to be authorised by a user gesture, and the authorisation is granted **per
 *    media element** — Safari's macOS policy hands it to the element whose `play()` is called
 *    while a tap is still being processed, and to no other. So there is exactly ONE element
 *    here, it is played on a real source inside the first tap (`unlock()`), and every clip
 *    afterwards reuses it. A source-less element played during the tap, or a fresh element per
 *    clip, authorises nothing — which is why this used to go silent on a MacBook.
 * 2. Loading is done with `fetch`, not with `<audio preload>`: iOS ignores `preload` on an audio
 *    element until it is played, and its media loader is picky about range requests. A fetched
 *    clip is kept as a blob URL, so playing it is a purely local operation.
 * 3. iOS caps how many media elements can be live at once. One is comfortably under it.
 *
 * Every public call reports how it actually ended (`PlayOutcome`) and is bounded by an absolute
 * deadline covering the whole journey — waiting for an announcement to finish, fetching,
 * decoding, playing. Nothing here rejects and nothing here hangs, so a screen that sequences
 * audio can always move on; and because a blocked clip no longer looks like a clip that played,
 * a game can tell the child to tap again instead of marking them wrong for silence.
 */
const IMAGE_RE = /\.(webp|png|jpe?g|gif|svg)(\?|$)/i;
const AUDIO_RE = /\.(mp3|m4a|aac|ogg|wav)(\?|$)/i;
/** Clips kept as blob URLs (LRU). 400 × ~15 KB is well under 10 MB. */
const BLOB_CAP = 400;
/** A clip that is not warmed yet gets this long to arrive by fetch before the element streams
 * it from the network instead. */
const FETCH_BEFORE_PLAY_MS = 2500;
/** A clip's whole journey: queueing behind an announcement, fetching, decoding, playing. */
const PLAY_MAX_MS = 15000;
/** The same, for a whole announce sequence — one or two clips, each of which may spend up to
 * FETCH_BEFORE_PLAY_MS arriving. */
const ANNOUNCE_MAX_MS = 20000;
/** What `loaded.url` says while the element holds the silent activation clip. */
const SILENCE = '\0silence';
/** Media fetches in flight, service-wide. Safari opens about six connections per origin over
 * HTTP/1.1 — which the dev and preview servers are — so past this the browser queues requests
 * somewhere this service can no longer reorder them. */
const MAX_FETCHES = 6;
/** Of those, the most that may be background warming, so a tap always finds a free connection
 * instead of queueing behind a lesson's worth of downloads. */
const MAX_WARMING = 3;
/** One clip's fetch. Without this a few stalled requests parked a whole queue behind them. */
const FETCH_TIMEOUT_MS = 8000;
/** Pictures are an order of magnitude bigger than clips, so they get longer. */
const IMAGE_TIMEOUT_MS = 20000;
/** After this many timeouts, background warming stops chasing a URL. A play still asks for it:
 * slow is not the same as broken. */
const WARM_GIVE_UP = 2;

/** Lanes, served in this order. */
const PRIO_PLAY = 0;
const PRIO_PRIME = 1;
const PRIO_WARM = 2;
/** Owner of everything the child is waiting on right now. Never cancelled; sessions start at 1. */
const FOREGROUND = 0;
/**
 * How many taps may be spent trying to authorise the element. A browser that has refused three
 * times is not going to change its mind on the fourth, and every attempt reloads a media element
 * — so without a bound, a device that really does refuse pays that cost on every tap for the
 * life of the page. Giving up costs the child nothing: a clip that will not play reports
 * `blocked`, which is what puts "no sound, tap to hear it" in front of them, and that tap is a
 * gesture of its own.
 */
const ACTIVATION_TRIES = 3;

/**
 * How long a clip will wait for the element to finish building its pipeline before taking it
 * over. WebKit's GStreamer media backend (Linux: GNOME Web, and the WebKit Playwright runs in CI)
 * can deadlock the page's main thread when `src` changes while the pipeline is being built: a
 * streaming thread holding a pad's lock posts a stream-collection message and waits for the main
 * thread to handle it, while the main thread, tearing the pipeline down for the new source, waits
 * for that lock. The whole tab stops, not just the sound. The pipeline is built on a source's
 * first load (usually the silent activation clip the tap that opened the screen just loaded)
 * and again on a seek of an element that has ended. Waiting for that to settle costs a few
 * hundred milliseconds at most; past this bound the clip goes ahead anyway, as it always used to.
 */
const SETTLE_BEFORE_SWAP_MS = 1500;

/** Building its pipeline: still fetching a source with nothing of it decoded yet, or seeking.
 * The states SETTLE_BEFORE_SWAP_MS is for. */
const pipelineBusy = (el: HTMLMediaElement) =>
  el.seeking ||
  (el.networkState === 2 /* NETWORK_LOADING */ && el.readyState === 0); /* HAVE_NOTHING */

/** How a clip ended. `completed` is the only one that means the child heard it. */
export type PlayOutcome =
  | { status: 'completed' }
  | { status: 'skipped' }
  | { status: 'blocked' }
  | { status: 'failed'; reason: string }
  | { status: 'timeout' };

const COMPLETED: PlayOutcome = { status: 'completed' };
const SKIPPED: PlayOutcome = { status: 'skipped' };
const BLOCKED: PlayOutcome = { status: 'blocked' };
const TIMEOUT: PlayOutcome = { status: 'timeout' };

/**
 * The clip did not come out: refused by the browser, broken, or out of time. A `skipped` clip is
 * deliberately not one of these — it was interrupted on purpose, by a newer clip or by the screen
 * going away, and whatever interrupted it reports for itself.
 */
export const wentSilent = (o: PlayOutcome): boolean =>
  o.status === 'blocked' || o.status === 'failed' || o.status === 'timeout';

/** What the element currently holds, so a repeat tap on the same clip replays it without
 * reloading, and so the blob cache never revokes the URL being played. */
interface Loaded {
  url: string;
  src: string;
  /**
   * Whether letting go of this source means releasing it. One rule, decided when the source is
   * handed to the element and updated if the cache drops it, rather than re-derived from
   * `blobs` afterwards — which got it wrong both ways round. It stays false for the activation
   * clip, which `unlock()` keeps to re-prime with, and for a clip the cache is still holding.
   */
  release: boolean;
}

/** One activity's background warming, for as long as the child is in it. */
export interface PreloadHandle {
  /** Abandon whatever this activity has not fetched yet and abort its fetches in flight. A clip
   * another activity — or the clip playing right now — still wants is left alone. */
  cancel(): void;
}

/** One clip or picture that is wanted but not yet cached: queued, or being fetched. */
interface Task {
  url: string;
  /** Pictures are warmed with an `<img>`, not a fetch: fetching a WebP any other way leaves the
   * browser with a cache entry `<img>` may not reuse. */
  kind: 'audio' | 'image';
  /** The best priority anyone has asked for. Only ever moves towards PRIO_PLAY. */
  prio: number;
  /** Sessions (and FOREGROUND) that still want this. Empty means nobody does. */
  owners: Set<number>;
  /** Resolve-only: nothing here rejects, so every waiter is released however it ends. */
  waiters: Array<() => void>;
  ctl: AbortController | null;
  img: HTMLImageElement | null;
  timer: ReturnType<typeof setTimeout> | null;
  /** Bumped by every start, so a callback from an attempt we abandoned can be told apart from a
   * real answer without inspecting the error. */
  gen: number;
  done: boolean;
}

/**
 * A fraction of a second of digital silence, as a WAV. Made here rather than fetched or
 * `data:`-encoded: the activation `play()` has to happen inside the tap, so there is no time for
 * a round trip, and WebKit's media loader is unreliable with `data:` URLs. Blob URLs are what
 * this file feeds the element all day anyway.
 */
function silentWav(): Blob {
  const rate = 8000;
  const frames = 400;
  const bytes = frames * 2;
  const buf = new ArrayBuffer(44 + bytes);
  const view = new DataView(buf);
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + bytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, bytes, true);
  // The samples are left at zero: silent, but a real decodable resource, which is the point.
  return new Blob([buf], { type: 'audio/wav' });
}

class AudioService {
  /** Non-audio URLs already warmed (images), so preload never touches them twice. */
  private warmed = new Set<string>();
  /** Clip URL → blob object URL, insertion order = least recently used first. */
  private blobs = new Map<string, string>();
  /** Everything wanted but not yet in `blobs`: queued or fetching, one entry per URL. This is
   * what the old per-call queues were, plus the work that has not started — which is what makes
   * the limit service-wide instead of four more requests per `preload()` call. */
  private tasks = new Map<string, Task>();
  /** Ready to start, by lane. A stale entry (promoted, arrived, abandoned) is skipped when it is
   * popped rather than hunted down at the time. */
  private queues: string[][] = [[], [], []];
  private live = new Set<Task>();
  /** URL → consecutive timeouts. Kept apart from `failed` on purpose: `failed` silences a clip
   * for the rest of the session, and a clip that is merely slow must never land there. */
  private slow = new Map<string, number>();
  private sessions = new Set<number>();
  private sessionSeq = 0;
  /** Failed fetches, so a broken clip is not retried on every play. */
  private failed = new Set<string>();
  /** The one element every clip plays through (see the note at the top of the file). */
  private el: HTMLAudioElement | null = null;
  private loaded: Loaded | null = null;
  private silentSrc: string | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  // Settles whatever operation owns the element; `halt()` must invoke it before taking the
  // channel over, otherwise a caller awaiting the interrupted clip would hang forever, since
  // pausing a clip fires neither `ended` nor `error`.
  private currentDone: ((outcome: PlayOutcome) => void) | null = null;
  /** Id of the operation that owns the element, 0 when idle. Anything global — a deadline
   * firing, a re-primed activation — checks this first so it cannot act on someone else's clip. */
  private op = 0;
  private opSeq = 0;
  // Bumped by every stop(): a play that was waiting on a fetch when the channel was stopped
  // must not start late; an announce sequence checks it between clips so a stop skips the rest.
  private epoch = 0;
  // While an announcement (the spoken instruction) is playing, ordinary play() calls wait for it
  // instead of cutting it off; only the most recent waiting call is honoured, so a question that
  // was skipped past meanwhile never speaks late.
  private gate: Promise<unknown> | null = null;
  private gateTicket = 0;
  /** Bumped only by `stop()`. `epoch` cannot serve here: an `announce()` bumps that too, and a
   * play parked behind an announcement is meant to speak once it is over, not be cancelled by
   * it. This is what tells a parked play that the screen it belonged to has gone. */
  private stops = 0;
  /** A gesture has happened, so a screen reached by that tap may speak on arrival. */
  unlocked = false;
  /** The element itself has been authorised — a stronger claim than `unlocked`, and the one
   * that decides whether the next gesture should try again. */
  activated = false;
  /** Gestures spent trying to authorise the element, capped at ACTIVATION_TRIES. */
  private tries = 0;
  /** True for the rest of a task that is handling a tap or key press (or that called
   * `unlock()`): a play made now is still inside the gesture, which an element not yet
   * authorised needs its `play()` to be. */
  private inGesture = false;

  constructor() {
    if (typeof speechSynthesis !== 'undefined') {
      const load = () => (this.voices = speechSynthesis.getVoices());
      load();
      speechSynthesis.addEventListener?.('voiceschanged', load);
    }
    // The first tap anywhere unlocks playback, so a screen reached by that tap (the gate) can
    // speak on arrival rather than waiting for a second tap on Start. It stays armed past that
    // one — a first tap the browser refuses is no longer the only one we ever get — but only
    // for a few, because each attempt reloads the element and a browser that has said no three
    // times has made itself clear.
    if (typeof document !== 'undefined') {
      const onTap = () => {
        this.unlock();
        if (this.activated || this.tries >= ACTIVATION_TRIES)
          document.removeEventListener('pointerdown', onTap, { capture: true });
      };
      document.addEventListener('pointerdown', onTap, { capture: true });
      // iOS does not count a touch's `pointerdown` as a gesture, only what follows it; a button
      // that plays on `click` without calling unlock() must still be seen as inside one.
      for (const type of ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'])
        document.addEventListener(type, () => this.markGesture(), { capture: true });
    }
  }

  private markGesture(): void {
    if (this.inGesture) return;
    this.inGesture = true;
    setTimeout(() => (this.inGesture = false), 0);
  }

  /**
   * Point the element at a source, releasing whatever it was holding if that was ours to
   * release. Every assignment to `this.loaded` goes through here: doing it in two places is
   * what left the activation clip revoked in one path and a re-fetched clip's first object URL
   * orphaned in the other.
   */
  private setLoaded(next: Loaded): void {
    const previous = this.loaded;
    this.loaded = next;
    if (previous && previous.src !== next.src && previous.release)
      URL.revokeObjectURL(previous.src);
  }

  /** The element, made on first use. Null only where there is no `Audio` at all (tests, SSR). */
  private element(): HTMLAudioElement | null {
    if (this.el) return this.el;
    if (typeof Audio === 'undefined') return null;
    this.el = new Audio();
    this.el.preload = 'auto';
    return this.el;
  }

  /**
   * Authorise playback from a user gesture. This has to play a REAL source on the ONE element
   * every clip will use, synchronously, while the tap is still being processed: that is the only
   * thing Safari accepts, and the authorisation does not transfer to any other element.
   */
  unlock(): void {
    sfx.unlock();
    this.unlocked = true;
    this.markGesture();
    if (this.activated || this.tries >= ACTIVATION_TRIES) return;
    // A retry must never interrupt a clip that is playing: the child hearing the word matters
    // more than re-attempting an authorisation the next tap can attempt just as well.
    if (this.op !== 0) return;
    const el = this.element();
    if (!el) return;
    this.tries++;
    log('audio', `unlock (attempt ${this.tries} of ${ACTIVATION_TRIES})`);
    try {
      if (!this.silentSrc) this.silentSrc = URL.createObjectURL(silentWav());
      el.pause();
      el.src = this.silentSrc;
      el.load();
      this.setLoaded({ url: SILENCE, src: this.silentSrc, release: false });
      const p = el.play();
      // Not muted on purpose: WebKit allows muted playback without a gesture, so a muted prime
      // can earn the muted-autoplay allowance instead of the one we need. Silent-but-unmuted is
      // inaudible either way and leaves no doubt about which permission was granted.
      if (p && typeof p.then === 'function')
        p.then(
          () => {
            this.activated = true;
            log('audio', 'unlock: element authorised');
          },
          (e: { name?: string }) => {
            // Aborted because a real clip took the element over is not a refusal. The grant is
            // earned the moment play() is called inside the gesture; loading the next source
            // over it rejects this promise but does not take the grant away. Only being told
            // no — NotAllowedError — means we still have nothing.
            if (e?.name === 'AbortError') {
              this.activated = true;
              log('audio', 'unlock: authorised, then taken over by a clip');
              return;
            }
            log('audio', `unlock: refused (${e?.name ?? '?'})`);
          },
        );
      else this.activated = true;
    } catch (e) {
      log('audio', `unlock threw: ${String(e)}`);
    }
    if (typeof speechSynthesis !== 'undefined') {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      speechSynthesis.speak(u);
    }
  }

  /**
   * Ask for one clip or picture, at a priority, on behalf of an owner. Resolves once it is
   * cached, or once it has failed or been abandoned — never rejects, so a caller can always move
   * on.
   *
   * This is the only way anything is fetched. Something already wanted at a lower priority is
   * promoted rather than downloaded twice, which is what lets a play take over a clip that
   * background warming is already halfway through.
   */
  private request(url: string, prio: number, owner: number, kind: Task['kind']): Promise<void> {
    if (this.blobs.has(url) || this.warmed.has(url) || this.failed.has(url))
      return Promise.resolve();
    // Background warming gives up on a URL that has been too slow twice, or one stalled clip is
    // re-queued for the rest of the session. A play may still ask for it.
    if (prio === PRIO_WARM && (this.slow.get(url) ?? 0) >= WARM_GIVE_UP) return Promise.resolve();
    let task = this.tasks.get(url);
    if (task) {
      task.owners.add(owner);
      if (prio < task.prio) {
        task.prio = prio;
        if (!this.live.has(task)) this.queues[prio]!.push(url);
      }
    } else {
      task = {
        url,
        kind,
        prio,
        owners: new Set([owner]),
        waiters: [],
        ctl: null,
        img: null,
        timer: null,
        gen: 0,
        done: false,
      };
      this.tasks.set(url, task);
      this.queues[prio]!.push(url);
    }
    const t = task;
    const wait = new Promise<void>((resolve) => t.waiters.push(resolve));
    this.pump();
    return wait;
  }

  private warming(): number {
    let n = 0;
    for (const t of this.live) if (t.prio === PRIO_WARM) n++;
    return n;
  }

  private pump(): void {
    // The clip the child is waiting on does not queue at all: making a tap wait behind a
    // lesson's worth of background warming is the whole complaint. This is the one lane allowed
    // past the limit, which is safe because it is a single channel — normally one clip at a
    // time, and at worst the handful a child can tap through in a second or two, each a few
    // tens of kilobytes. Superseding a clip does not cancel its fetch: the bytes are wanted
    // either way, and the cache is what the next tap reads from.
    for (const url of this.queues[PRIO_PLAY]!.splice(0)) {
      const t = this.tasks.get(url);
      if (t && !t.done && !this.live.has(t)) this.start(t);
    }
    while (this.live.size < MAX_FETCHES) {
      const t = this.take();
      if (!t) return;
      this.start(t);
    }
  }

  private take(): Task | undefined {
    for (const prio of [PRIO_PRIME, PRIO_WARM]) {
      if (prio === PRIO_WARM && this.warming() >= MAX_WARMING) continue;
      const q = this.queues[prio]!;
      while (q.length) {
        const url = q.shift();
        if (url === undefined) break;
        const t = this.tasks.get(url);
        // Skipped here rather than hunted down at the time: arrived, abandoned, promoted into a
        // higher lane, or already running under an entry this one duplicates.
        if (t && t.prio === prio && !t.done && !this.live.has(t)) return t;
      }
    }
    return undefined;
  }

  private start(task: Task): void {
    const gen = ++task.gen;
    const stale = () => task.done || task.gen !== gen;
    const name = task.url.split('/').pop();
    this.live.add(task);
    task.timer = setTimeout(
      () => {
        if (stale()) return;
        // Out of time, not broken. `failed` silences a clip for the rest of the session, so a
        // clip that is only slow must never go there; this is counted instead, and background
        // warming stops chasing it after a couple of goes while a play still asks.
        this.slow.set(task.url, (this.slow.get(task.url) ?? 0) + 1);
        log('audio', `fetch timed out ${name}`);
        this.abandon(task);
        this.finish(task);
      },
      task.kind === 'image' ? IMAGE_TIMEOUT_MS : FETCH_TIMEOUT_MS,
    );
    if (task.kind === 'image') {
      const img = new Image();
      task.img = img;
      img.onload = () => {
        if (stale()) return;
        // Only once it has actually arrived: marking it warmed up front meant a picture whose
        // load was interrupted was never fetched again for the rest of the session.
        this.warmed.add(task.url);
        this.finish(task);
      };
      img.onerror = () => {
        if (stale()) return;
        this.failed.add(task.url);
        this.finish(task);
      };
      img.src = task.url;
      return;
    }
    const ctl = new AbortController();
    task.ctl = ctl;
    void fetch(task.url, { signal: ctl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (stale()) return;
        this.remember(task.url, URL.createObjectURL(blob));
        this.slow.delete(task.url);
        this.finish(task);
      })
      .catch((e: unknown) => {
        if (stale()) return;
        // An abort is this service's own doing — a timeout, or the activity being left — and
        // must not be remembered as a broken clip.
        if (!ctl.signal.aborted) {
          this.failed.add(task.url);
          log('audio', `fetch failed ${name}: ${String(e)}`);
        }
        this.finish(task);
      });
  }

  /** Stop whatever this task has in flight, without settling it. */
  private abandon(task: Task): void {
    task.ctl?.abort();
    if (task.img) {
      task.img.onload = null;
      task.img.onerror = null;
      task.img.removeAttribute('src');
    }
  }

  private finish(task: Task): void {
    if (task.done) return;
    task.done = true;
    if (task.timer !== null) clearTimeout(task.timer);
    task.timer = null;
    task.ctl = null;
    task.img = null;
    this.live.delete(task);
    if (this.tasks.get(task.url) === task) this.tasks.delete(task.url);
    const waiters = task.waiters;
    task.waiters = [];
    for (const w of waiters) w();
    this.pump();
  }

  private remember(url: string, objectUrl: string): void {
    if (this.blobs.has(url)) {
      URL.revokeObjectURL(objectUrl);
      return;
    }
    while (this.blobs.size >= BLOB_CAP) {
      const [oldest, old] = this.blobs.entries().next().value as [string, string];
      // The element still holding it keeps the blob alive until it loads something else — and
      // from here that is the only thing holding it, which is recorded now rather than worked
      // out later from a cache that may have taken the same URL back in the meantime.
      if (this.loaded?.src === old) this.loaded.release = true;
      else URL.revokeObjectURL(old);
      this.blobs.delete(oldest);
    }
    this.blobs.set(url, objectUrl);
  }

  /**
   * Warm the cache for one activity's clips and pictures, in the order given, in the background.
   *
   * The returned handle must be cancelled when the activity is left. Until it was, every call
   * started its own four workers that nothing could stop: two activities meant eight live
   * fetches, and finishing one of an abandoned session's downloads simply started another, all
   * of it competing with whatever the child had moved on to. Note that `stop()` cannot be that
   * cancellation point — it fires at every question boundary, and would throw away the download
   * of question four while question three is being answered.
   */
  preload(refs: Iterable<MediaRef | undefined>): PreloadHandle {
    const session = ++this.sessionSeq;
    this.sessions.add(session);
    const queued = new Set<string>();
    let n = 0;
    for (const r of refs) {
      if (!r || r.kind !== 'url' || queued.has(r.url)) continue;
      queued.add(r.url);
      const image = IMAGE_RE.test(r.url);
      if (!image && !AUDIO_RE.test(r.url)) {
        this.warmed.add(r.url);
        continue;
      }
      void this.request(r.url, PRIO_WARM, session, image ? 'image' : 'audio');
      n++;
    }
    if (n) log('audio', `preload: ${n} files`);
    return {
      cancel: () => {
        if (!this.sessions.delete(session)) return;
        const orphans: Task[] = [];
        for (const task of this.tasks.values()) {
          // Still wanted by the clip playing, or by another activity: leave it alone.
          if (!task.owners.delete(session) || task.owners.size > 0) continue;
          orphans.push(task);
          // Out of the running before any of them is settled. `finish()` pumps the queues, so
          // settling one partway through this loop would start the very downloads being
          // called off — and a queue entry whose task is gone is skipped when it is popped.
          this.tasks.delete(task.url);
          this.live.delete(task);
        }
        for (const task of orphans) {
          this.abandon(task);
          this.finish(task);
        }
        if (orphans.length) log('audio', `preload: dropped ${orphans.length} files on leaving`);
      },
    };
  }

  /**
   * Fetch a handful of clips right now and resolve once they have all arrived (or after
   * `timeoutMs`, so a slow connection delays a game rather than blocking it). Call it from a tap
   * for the few clips the next screen plays back to back, so none of them stalls mid-sequence.
   * These sit in their own lane, ahead of background warming and behind the clip playing.
   */
  prime(refs: Iterable<MediaRef | undefined>, timeoutMs = 6000): Promise<void> {
    const waits: Promise<void>[] = [];
    for (const r of refs) {
      if (!r || r.kind !== 'url' || !AUDIO_RE.test(r.url)) continue;
      if (this.blobs.has(r.url) || this.failed.has(r.url)) continue;
      waits.push(this.request(r.url, PRIO_PRIME, FOREGROUND, 'audio'));
    }
    if (waits.length === 0) {
      log('audio', 'prime: nothing to fetch');
      return Promise.resolve();
    }
    const started = performance.now();
    return Promise.race([
      Promise.all(waits).then(() =>
        log(
          'audio',
          `prime: ${waits.length} clips ready in ${Math.round(performance.now() - started)}ms`,
        ),
      ),
      new Promise<void>((resolve) =>
        setTimeout(() => {
          log('audio', `prime: gave up after ${timeoutMs}ms (${waits.length} clips)`);
          resolve();
        }, timeoutMs),
      ),
    ]);
  }

  stop(): void {
    this.epoch++;
    this.stops++;
    // Drop the gate before halting, so a play() issued right after this stop goes straight
    // through instead of parking behind an announcement that is already over.
    this.gate = null;
    this.halt('stop: interrupting a clip in flight');
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
  }

  /** Silence the channel without ending an announce sequence (used between its clips). */
  private halt(why: string): void {
    if (this.currentDone) {
      log('audio', why);
      const done = this.currentDone;
      this.currentDone = null;
      // Interrupted, not finished: a game that gates on hearing a clip must not count this one.
      done(SKIPPED);
    }
    this.hush();
  }

  /** Stop the element where it is, leaving its source loaded and ready to replay. */
  private hush(): void {
    const el = this.el;
    if (!el || el.paused) return;
    // Not rewound here: playUrl() rewinds whatever it plays. Seeking an element that has already
    // ended makes WebKit's GStreamer backend rebuild its pipeline, and the next clip's `src`
    // landing in the middle of that rebuild is the deadlock SETTLE_BEFORE_SWAP_MS describes.
    el.pause();
  }

  /** Settle `p`, or give up at `until`. The timer is always cleared, so a clip that runs its
   * full length leaves no timer behind per waiter. */
  private within(p: Promise<unknown>, until: number): Promise<'done' | 'timeout'> {
    const ms = until - performance.now();
    if (ms <= 0) return Promise.resolve('timeout');
    return new Promise<'done' | 'timeout'>((resolve) => {
      const t = setTimeout(() => resolve('timeout'), ms);
      const settle = () => {
        clearTimeout(t);
        resolve('done');
      };
      void p.then(settle, settle);
    });
  }

  /**
   * Play one clip and resolve with how it ended. Never rejects and never hangs: the whole
   * journey — waiting for an announcement, fetching, decoding, playing — is bounded by `maxMs`
   * (default 15 s), so a screen that sequences audio can always move on. Games that pace
   * themselves on clips pass a tighter cap.
   */
  async play(
    ref: MediaRef | undefined,
    opts: { maxMs?: number; onStart?: () => void } = {},
  ): Promise<PlayOutcome> {
    if (!ref) return SKIPPED;
    const until = performance.now() + (opts.maxMs ?? PLAY_MAX_MS);
    const label =
      ref.kind === 'url' ? ref.url.split('/').pop() : `tts:${ref.kind === 'tts' ? ref.text : '?'}`;
    const started = performance.now();
    const stops = this.stops;
    // In a loop, not a single await: an announcement that starts while this call is parked
    // replaces the gate, and resuming on the old one would cut the new announcement off.
    while (this.gate) {
      const gate = this.gate;
      const ticket = ++this.gateTicket;
      const waited = await this.within(gate, until);
      // A later play() has taken the channel: this question was skipped past, so stay silent.
      if (ticket !== this.gateTicket) return SKIPPED;
      // The channel was stopped while this call was parked — the screen that asked for it has
      // gone. Waiting on the gate must not outlive a stop, or the clip speaks over whatever
      // replaced it.
      if (this.stops !== stops) return SKIPPED;
      if (waited === 'timeout') {
        log('audio', `TIMEOUT ${label} waiting for an announcement`);
        return TIMEOUT;
      }
      if (this.gate === gate) this.gate = null;
    }
    let outcome: PlayOutcome;
    try {
      outcome = await this.playNow(ref, undefined, until, opts.onStart);
    } catch (e) {
      log('audio', `play threw ${label}: ${String(e)}`);
      return { status: 'failed', reason: String(e) };
    }
    log('audio', `${outcome.status} ${label} after ${Math.round(performance.now() - started)}ms`);
    return outcome;
  }

  /**
   * Speak a sequence (e.g. the instruction in English, then Chinese) right away, holding every
   * ordinary play() until it has finished. Resolves with how the last clip ended; a `stop()` in
   * the middle skips the rest of the sequence. Bounded like `play()`, so an announcement that
   * never receives a terminal media event releases the gate instead of jamming the channel —
   * which used to leave the whole screen waiting on it forever.
   */
  announce(refs: Array<MediaRef | undefined>, opts: { maxMs?: number } = {}): Promise<PlayOutcome> {
    const epoch = ++this.epoch;
    const until = performance.now() + (opts.maxMs ?? ANNOUNCE_MAX_MS);
    const run = (async (): Promise<PlayOutcome> => {
      let last: PlayOutcome = SKIPPED;
      for (const ref of refs) {
        if (!ref) continue;
        if (this.epoch !== epoch) return SKIPPED;
        if (performance.now() >= until) return TIMEOUT;
        last = await this.playNow(ref, epoch, until);
        if (last.status === 'timeout') return last;
      }
      return last;
    })().catch((e: unknown): PlayOutcome => ({ status: 'failed', reason: String(e) }));
    const gate: Promise<PlayOutcome> = run.finally(() => {
      // Identity-checked: a newer announcement must not have its gate cleared by an older one.
      if (this.gate === gate) this.gate = null;
    });
    this.gate = gate;
    return gate;
  }

  private playNow(
    ref: MediaRef,
    epoch: number | undefined,
    until: number,
    onStart?: () => void,
  ): Promise<PlayOutcome> {
    // Inside an announce sequence the epoch is already the sequence's own; a full stop() here
    // would end it after its first clip.
    if (epoch === undefined) this.epoch++;
    const mine = epoch ?? this.epoch;
    this.halt('interrupting a clip in flight');
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    if (ref.kind === 'url') return this.playUrl(ref.url, mine, until, onStart);
    if (ref.kind === 'tts') return this.speakNow(ref.text, ref.lang, until, onStart);
    return Promise.resolve(SKIPPED);
  }

  /**
   * Resolve once the element is not in the middle of its first load of a source (see
   * SETTLE_BEFORE_SWAP_MS), or after that bound or the caller's deadline, whichever is first.
   * True if it settled, false if it was given up on.
   */
  private settleLoad(el: HTMLAudioElement, until: number): Promise<boolean> {
    if (!pipelineBusy(el)) return Promise.resolve(true);
    return new Promise((resolve) => {
      // Not `abort` or `emptied`: those mean a new load has just begun over this one (an
      // unlock re-priming the element), which is the state being waited out, not its end.
      const events = ['loadedmetadata', 'seeked', 'error'] as const;
      const done = (settled: boolean) => {
        clearTimeout(timer);
        for (const e of events) el.removeEventListener(e, onEvent);
        resolve(settled);
      };
      const onEvent = () => {
        if (!pipelineBusy(el)) done(true);
      };
      for (const e of events) el.addEventListener(e, onEvent);
      const timer = setTimeout(
        () => done(false),
        Math.max(0, Math.min(SETTLE_BEFORE_SWAP_MS, until - performance.now())),
      );
    });
  }

  private async playUrl(
    url: string,
    epoch: number,
    until: number,
    onStart?: () => void,
  ): Promise<PlayOutcome> {
    const name = url.split('/').pop();
    // Give a clip that has not arrived yet a moment to come in by fetch, which is far more
    // reliable on iOS than letting the element stream it; then play whatever we have. Nothing
    // is awaited on the cached path, so a play issued from a tap still carries the gesture.
    if (!this.blobs.has(url) && !this.failed.has(url)) {
      const t0 = performance.now();
      await this.within(
        this.request(url, PRIO_PLAY, FOREGROUND, 'audio'),
        Math.min(until, t0 + FETCH_BEFORE_PLAY_MS),
      );
      if (this.epoch !== epoch) {
        log('audio', `skip ${name}: stopped while fetching`);
        return SKIPPED;
      }
      // The caller's deadline ran out while we were fetching. Stop here rather than starting a
      // clip nobody is waiting for any more: this is the late playback the timeout used to leave
      // running behind whatever came next.
      if (performance.now() >= until) {
        log('audio', `TIMEOUT ${name} while fetching`);
        return TIMEOUT;
      }
      log(
        'audio',
        `${this.blobs.has(url) ? 'fetched' : 'not fetched'} ${name} in ${Math.round(performance.now() - t0)}ms`,
      );
    }
    const el = this.element();
    if (!el) return { status: 'failed', reason: 'no Audio constructor' };
    const src = this.blobs.get(url) ?? url;
    // Inside a gesture, an element that is not authorised yet needs this play() now or never:
    // Safari's grant is earned there, and a silent element is worse than the rare hang. In the
    // tap that ran unlock() the element has not even started its load yet. Anywhere else there
    // is no gesture left to lose.
    if (this.loaded?.src !== src && (this.activated || !this.inGesture) && pipelineBusy(el)) {
      const settled = await this.settleLoad(el, until);
      if (this.epoch !== epoch) {
        log('audio', `skip ${name}: stopped while the element settled`);
        return SKIPPED;
      }
      // As after a fetch: a clip nobody is waiting for any more must not take the element.
      if (performance.now() >= until) {
        log('audio', `TIMEOUT ${name} while the element settled`);
        return TIMEOUT;
      }
      if (!settled) log('audio', `element still loading after ${SETTLE_BEFORE_SWAP_MS}ms`);
    }
    const id = ++this.opSeq;
    this.op = id;
    log('audio', `play ${name} (${src.startsWith('blob:') ? 'blob' : 'network'})`);
    return new Promise<PlayOutcome>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      const onEnded = () => {
        log('audio', `ended ${name}`);
        finish(COMPLETED);
      };
      const onError = () => {
        // `load()` clears `error` and fires its own tasks asynchronously, so an event left over
        // from the previous clip can land here after this one attached its listeners.
        const code = el.error?.code;
        if (code === undefined) return;
        log('audio', `element error ${name}: code ${code}`);
        // MEDIA_ERR_ABORTED is us taking the element over, not a broken clip.
        finish(code === 1 ? SKIPPED : { status: 'failed', reason: `media error ${code}` });
      };
      const finish = (outcome: PlayOutcome) => {
        if (settled) return;
        settled = true;
        if (timer !== undefined) clearTimeout(timer);
        el.removeEventListener('ended', onEnded);
        el.removeEventListener('error', onError);
        if (this.currentDone === finish) this.currentDone = null;
        if (this.op === id) this.op = 0;
        resolve(outcome);
      };
      this.currentDone = finish;
      el.addEventListener('ended', onEnded);
      el.addEventListener('error', onError);
      timer = setTimeout(
        () => {
          log('audio', `TIMEOUT ${name} while playing`);
          // Silence it only if it still owns the element: a newer clip must not be cut off by
          // an older one's deadline.
          if (this.op === id) this.hush();
          finish(TIMEOUT);
        },
        Math.max(0, until - performance.now()),
      );
      try {
        // Reloading the source the element already holds would throw away a decoded clip for
        // nothing — which is exactly the repeat tap on the same word.
        if (this.loaded?.src !== src) {
          el.pause();
          el.src = src;
          el.load();
          // A source the cache holds is the cache's to release, not ours — until `remember()`
          // evicts it, which is the moment that flips this.
          this.setLoaded({ url, src, release: false });
        }
        // Safari can refuse a seek on an element that has not loaded yet; the clip then simply
        // plays from wherever it is (the start, for a freshly loaded source).
        try {
          el.currentTime = 0;
        } catch {
          /* play from the current position */
        }
        const p = el.play();
        if (p && typeof p.then === 'function')
          p.then(
            () => {
              log('audio', `playing ${name}`);
              // Sound is coming out of the element now. A caller that grades on whether the
              // child heard the prompt needs this moment, not `ended`: the clip runs for a
              // second or more, and an answer given part-way through it was still answered
              // with the word audible.
              if (!settled) onStart?.();
            },
            (e: { name?: string; message?: string }) => {
              log('audio', `play() rejected ${name}: ${e?.name ?? ''} ${e?.message ?? String(e)}`);
              // Refused for want of a gesture is the one a "tap to hear it" control can fix;
              // aborted is us taking the element over for the next clip.
              finish(
                e?.name === 'NotAllowedError'
                  ? BLOCKED
                  : e?.name === 'AbortError'
                    ? SKIPPED
                    : { status: 'failed', reason: e?.name ?? String(e) },
              );
            },
          );
      } catch (e) {
        log('audio', `play() threw ${name}: ${String(e)}`);
        finish({ status: 'failed', reason: String(e) });
      }
    });
  }

  /**
   * Speak a line in the browser's own voice, taking the channel as any clip would. Every other
   * route to the element goes through `playNow`, which halts and settles whatever was playing
   * first; this is the public one, so it has to do the same — otherwise the clip it talks over
   * keeps its listeners on the shared element and its caller waits out the whole deadline.
   */
  speak(
    text: string,
    lang: 'zh-CN' | 'en-SG' | 'en-US' = 'zh-CN',
    maxMs = PLAY_MAX_MS,
  ): Promise<PlayOutcome> {
    this.epoch++;
    this.halt('interrupting a clip in flight');
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    return this.speakNow(text, lang, performance.now() + maxMs);
  }

  private speakNow(
    text: string,
    lang: 'zh-CN' | 'en-SG' | 'en-US' = 'zh-CN',
    until?: number,
    onStart?: () => void,
  ): Promise<PlayOutcome> {
    if (typeof speechSynthesis === 'undefined') return Promise.resolve(SKIPPED);
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    u.rate = lang.startsWith('zh') ? 0.8 : 0.95;
    const prefix = lang.slice(0, 2);
    const voice =
      this.voices.find((v) => v.lang.replace('_', '-') === lang) ??
      this.voices.find((v) => v.lang.startsWith(prefix));
    if (voice) u.voice = voice;
    return new Promise<PlayOutcome>((resolve) => {
      let settled = false;
      let watchdog: ReturnType<typeof setTimeout> | undefined;
      let deadline: ReturnType<typeof setTimeout> | undefined;
      const finish = (outcome: PlayOutcome) => {
        if (settled) return;
        settled = true;
        if (watchdog !== undefined) clearTimeout(watchdog);
        if (deadline !== undefined) clearTimeout(deadline);
        if (this.currentDone === finish) this.currentDone = null;
        resolve(outcome);
      };
      this.currentDone = finish;
      u.onstart = () => {
        if (!settled) onStart?.();
      };
      u.onend = () => finish(COMPLETED);
      u.onerror = (e: SpeechSynthesisErrorEvent) =>
        finish(
          e.error === 'canceled' || e.error === 'interrupted'
            ? SKIPPED
            : { status: 'failed', reason: e.error },
        );
      speechSynthesis.speak(u);
      // Safari sometimes never fires onend. This one reports `completed`, not a timeout: the
      // line was almost certainly spoken, and calling it unheard would have a listening game
      // mark the child wrong for a Safari bug.
      watchdog = setTimeout(() => finish(COMPLETED), Math.min(8000, 800 + text.length * 350));
      // The caller's own deadline is a different matter: that one really is out of time.
      if (until !== undefined)
        deadline = setTimeout(
          () => {
            speechSynthesis.cancel();
            finish(TIMEOUT);
          },
          Math.max(0, until - performance.now()),
        );
    });
  }
}

export const audio = new AudioService();
